import React, { useEffect, useRef, useState } from 'react';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';
import Dialog from '@smart-tv/components/dialog';
import Typography from '@smart-tv/components/typography';

import styles from './dialog.module.scss';
import { FocusKeysRecordingDialog } from '../program-sheet/sheet-helpers';
import ProgressSpinner from '@smart-tv/components/progress-spinner';

export interface Props {
  dialogProps: RecordingDialogContentProps | null;
  onClose: () => void;
}

export interface RecordingDialogContentProps {
  titleText: string;
  primaryButtonText: string;
  primaryButtonFunction: () => void;
  secondaryButtonText?: string;
  secondaryButtonFunction?: () => void;
  description?: string;
}

interface RecordingDialogContentState {
  isPerformingPrimaryAction: boolean;
  setIsPerformingPrimaryAction: React.Dispatch<React.SetStateAction<boolean>>;
  isPerformingSecondaryAction: boolean;
  setIsPerformingSecondaryAction: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RecordingDialogContentComponentProps {
  dialogProps: RecordingDialogContentProps;
  state: RecordingDialogContentState;
}

const RecordingDialogContent = ({ dialogProps, state }: RecordingDialogContentComponentProps) => {
  const {
    titleText,
    primaryButtonText,
    secondaryButtonText,
    description,
    primaryButtonFunction,
    secondaryButtonFunction
  } = dialogProps;
  const {
    isPerformingPrimaryAction,
    setIsPerformingPrimaryAction,
    isPerformingSecondaryAction,
    setIsPerformingSecondaryAction
  } = state;
  const hasFocusedBefore = useRef(false);

  const { ref, focusSelf, focusKey, hasFocusedChild } = useFocusable({
    isFocusBoundary: true,
    focusable: false,
    trackChildren: true
  });

  useEffect(() => {
    focusSelf();
    return () => {
      setIsPerformingPrimaryAction(false);
      setIsPerformingSecondaryAction(false);
    };
  }, [focusSelf, setIsPerformingPrimaryAction, setIsPerformingSecondaryAction]);

  useEffect(() => {
    if (hasFocusedChild) {
      hasFocusedBefore.current = true;
    } else if (hasFocusedBefore.current) {
      focusSelf();
    }
  }, [hasFocusedChild, focusSelf]);

  const disableButtons = isPerformingPrimaryAction || isPerformingSecondaryAction;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['sheet-recording-dialog']} ref={ref}>
        <Typography
          className={styles['sheet-recording-dialog__title']}
          variant="h1"
          component="div"
        >
          {titleText}
        </Typography>
        {description && <Typography>{description}</Typography>}

        <Button
          focusKey={FocusKeysRecordingDialog.SINGLE}
          className={styles['sheet-recording-dialog__button']}
          disabled={disableButtons}
          onClick={() => {
            setIsPerformingPrimaryAction(true);
            primaryButtonFunction();
          }}
          icon={isPerformingPrimaryAction ? <ProgressSpinner icon={true} /> : undefined}
        >
          {primaryButtonText}
        </Button>
        {secondaryButtonText && secondaryButtonFunction && (
          <Button
            focusKey={FocusKeysRecordingDialog.SERIES}
            className={styles['sheet-recording-dialog__button']}
            disabled={disableButtons}
            onClick={() => {
              setIsPerformingSecondaryAction(true);
              secondaryButtonFunction();
            }}
            icon={isPerformingSecondaryAction ? <ProgressSpinner icon={true} /> : undefined}
          >
            {secondaryButtonText}
          </Button>
        )}
      </div>
    </FocusContext.Provider>
  );
};

export const RecordingDialog = (props: Props) => {
  const { dialogProps, onClose } = props;
  const [isPerformingPrimaryAction, setIsPerformingPrimaryAction] = useState(false);
  const [isPerformingSecondaryAction, setIsPerformingSecondaryAction] = useState(false);

  if (!dialogProps) return null;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      disableEscapeKeyDown={isPerformingPrimaryAction || isPerformingSecondaryAction}
      showCloseButton={true}
    >
      <RecordingDialogContent
        dialogProps={dialogProps}
        state={{
          isPerformingPrimaryAction,
          setIsPerformingPrimaryAction,
          isPerformingSecondaryAction,
          setIsPerformingSecondaryAction
        }}
      />
    </Dialog>
  );
};

export default RecordingDialog;
